import React from 'react';
import { Box, Button, VStack, Text, HStack, Image } from '@chakra-ui/react';
import { motion, useReducedMotion } from 'framer-motion';
import LivingNetworkCanvas from '../LivingNetworkCanvas';
import { trackEvent } from '../../utils/analytics';
import { useJoin } from '../../context/JoinContext';
import logo from '../../assets/yetzira.png';

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);

export default function HeroSection() {
  const prefersReduced = useReducedMotion();
  const { openJoin } = useJoin();

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.18,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = prefersReduced
    ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } }
    : {
        hidden: { opacity: 0, y: 60 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } },
      };

  const handleScrollToEngine = () => {
    trackEvent('cta_click', { cta: 'see_how_it_grows_hero' });
    const el = document.getElementById('exponential');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleJoin = () => {
    trackEvent('cta_click', { cta: 'join', source: 'hero' });
    openJoin();
  };

  return (
    <Box
      as="section"
      id="hero"
      position="relative"
      minH="100vh"
      display="flex"
      alignItems="center"
      overflow="hidden"
      bg="bg.900"
      aria-label="Hero section"
    >
      {/* Canvas background */}
      <LivingNetworkCanvas />

      {/* Gradient overlay for text legibility */}
      <Box
        position="absolute"
        inset={0}
        zIndex={1}
        bgGradient="linear(to-r, rgba(10,11,15,0.92) 45%, rgba(10,11,15,0.4) 100%)"
        aria-hidden="true"
      />

      {/* Content */}
      <Box
        position="relative"
        zIndex={2}
        maxW="1200px"
        mx="auto"
        px={{ base: '24px', md: '48px' }}
        w="100%"
        pt={{ base: '100px', md: '0' }}
        pb={{ base: '80px', md: '0' }}
      >
        <MotionVStack
          align="flex-start"
          spacing={{ base: 6, md: 8 }}
          maxW={{ base: '100%', md: '620px' }}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Logo */}
          <MotionBox variants={itemVariants}>
            <Image
              src={logo}
              alt="Yetzira logo"
              h={{ base: '64px', md: '88px' }}
              w="auto"
              objectFit="contain"
              sx={{ filter: 'drop-shadow(0 0 24px rgba(56,189,248,0.25))' }}
            />
          </MotionBox>

          {/* Kicker */}
          <MotionBox variants={itemVariants}>
            <Text
              fontFamily="'JetBrains Mono', monospace"
              fontWeight="500"
              fontSize="0.8rem"
              textTransform="uppercase"
              letterSpacing="1px"
              color="brand.300"
            >
              Autonomous AI Organizations
            </Text>
          </MotionBox>

          {/* Headline */}
          <MotionBox variants={itemVariants}>
            <Text
              as="h1"
              fontFamily="'Space Grotesk', sans-serif"
              fontWeight="700"
              fontSize={{ base: 'clamp(2.2rem, 8vw, 3.8rem)', md: 'clamp(2.5rem, 4vw, 4rem)' }}
              lineHeight="1.1"
              letterSpacing="-0.03em"
              color="text.primary"
            >
              Don't chase incremental AI.{' '}
              <Box as="span" color="brand.400">
                Exponential growth wins every time.
              </Box>
            </Text>
          </MotionBox>

          {/* Subheadline */}
          <MotionBox variants={itemVariants}>
            <Text
              fontSize={{ base: '1.05rem', md: '1.2rem' }}
              lineHeight="1.65"
              color="text.muted"
              maxW="540px"
            >
              Yetzira is a self-building platform that designs, deploys, and runs fully
              autonomous AI organizations — a workforce that creates and improves itself, and
              grows exponentially.
            </Text>
          </MotionBox>

          {/* CTAs */}
          <MotionBox variants={itemVariants} pt={2}>
            <HStack spacing={4} flexWrap="wrap" gap={3}>
              <Button
                variant="gold"
                size="lg"
                px={8}
                w={{ base: '100%', sm: 'auto' }}
                onClick={handleJoin}
                aria-label="Join Yetzira and create a new organization"
              >
                Join us
              </Button>
              <Button
                variant="outline_brand"
                size="lg"
                px={8}
                w={{ base: '100%', sm: 'auto' }}
                onClick={handleScrollToEngine}
                aria-label="See how yetzira.ai grows exponentially"
              >
                See how it grows →
              </Button>
            </HStack>
          </MotionBox>
        </MotionVStack>
      </Box>

      {/* Scroll indicator */}
      <Box
        position="absolute"
        bottom="32px"
        left="50%"
        transform="translateX(-50%)"
        zIndex={2}
        opacity={0.4}
        aria-hidden="true"
      >
        <motion.div
          animate={prefersReduced ? {} : { y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
        >
          <Box
            w="24px"
            h="40px"
            border="1px solid rgba(255,255,255,0.2)"
            borderRadius="12px"
            display="flex"
            justifyContent="center"
            pt="6px"
          >
            <Box w="3px" h="8px" bg="brand.400" borderRadius="2px" opacity={0.7} />
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
}
